import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import concessionFeeLogo from "../../../assets/images/concession-fee-logo.svg";
import CommercialAirlineNavigationBar from "../../../components/element/CommercialAirlineNavigationBar";
import Pagination from "../../../components/element/Pagination";
import { useFormData } from "../../../helpers/FormDataProvider";
import { StorageContext } from "../../../services/StorageProvider";
import useAirlineAdminApi from "../../../services/api/useAirlineAdminApi";
import { PAGINATION_PAGE_SIZE } from "../../../utils/constants";
import ROUTE_PATHS from "../../../utils/routePaths";
import ConcessionFeeTable from "./Tables/ConcessionFeeTable";
import ConcessionFilter from "./Views/ConcessionFilter";
import "./css/ConfessionFee.css";

const ConcessionFee = () => {
  const [concessionFeeData, setConcessionFeeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(PAGINATION_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state (what user selects)
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [customerId, setCustomerId] = useState("");
  const [submissionDate, setSubmissionDate] = useState(null);

  // Applied filter state (what's actually used for API calls)
  const [appliedAirline, setAppliedAirline] = useState(null);
  const [appliedCustomerId, setAppliedCustomerId] = useState("");
  const [appliedSubmissionDate, setAppliedSubmissionDate] = useState(null);

  const { userData } = useFormData();

  const { getConcessionFee } = useAirlineAdminApi(userData?.isAAUser);
  const { associatedAirlineData } = useContext(StorageContext);

  const fetchConcessionFeeData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const airlineId = appliedAirline?.id || userData?.airlineId || null;
      const formattedSubmissionDate = appliedSubmissionDate ? dayjs(appliedSubmissionDate).format('YYYY-MM-DD') : null;

      const response = await getConcessionFee(
        airlineId,
        appliedCustomerId || null,
        formattedSubmissionDate,
        currentPage,
        pageSize
      );

      setConcessionFeeData(response?.data?.content || []);
      setTotalPages(response?.data?.totalPages || 0);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConcessionFeeData();
  }, [currentPage, appliedAirline, appliedCustomerId, appliedSubmissionDate]);

  // Initial data fetch on component mount
  useEffect(() => {
    fetchConcessionFeeData();
  }, []);

  const handleClearFilter = () => {
    setSelectedAirline(null);
    setCustomerId("");
    setSubmissionDate(null);
    setAppliedAirline(null);
    setAppliedCustomerId("");
    setAppliedSubmissionDate(null);
    setCurrentPage(0);
  };

  const handleApplyFilter = (newCustomerId, newSubmissionDate, newSelectedAirline) => {
    setAppliedCustomerId(newCustomerId);
    setAppliedSubmissionDate(newSubmissionDate);
    setAppliedAirline(newSelectedAirline);
    setCurrentPage(0);
  };

  const handleAirlineChange = (airline) => {
    setSelectedAirline(airline);
    // Don't automatically apply the filter - wait for Apply button
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div>
      <div className="concession-fee-container">
        <CommercialAirlineNavigationBar
          title={"Concession Fee"}
          fullWidthContent={true}
          navigationLogo={concessionFeeLogo}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              Concession Fee
            </div>
          }
        />
      </div>
      <ConcessionFilter
        onClickClear={handleClearFilter}
        onClickApply={handleApplyFilter}
        onAirlineChange={handleAirlineChange}
        selectedAirline={selectedAirline}
      />
      <ConcessionFeeTable
        concessionFeeData={concessionFeeData}
        isLoading={isLoading}
        error={error}
      />
      {!isLoading && !error && concessionFeeData.length > 0 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          onPrevPage={handlePreviousPage}
          onNextPage={handleNextPage}
        />
      )}
    </div>
  );
};

export default ConcessionFee;
